import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Calendar, UtensilsCrossed, Phone, Sparkles, Check, Users, ChefHat, Leaf, Baby, ArrowRight, Crown, Clock, Star } from 'lucide-react';
import { Button } from '../components/ui/button';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_hibachi-feasts/artifacts/zkwjbg2h_Royal%20Delights%20Logo.png";
const HERO_IMAGE = "https://images.unsplash.com/photo-1663530761401-15eefb544889?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwxfHxjaGVmJTIwcGxhdGluZyUyMGdvdXJtZXQlMjBmb29kJTIwY2xvc2UlMjB1cCUyMGRhcmslMjBiYWNrZ3JvdW5kfGVufDB8fHx8MTc3MDg2NTMyNnww&ixlib=rb-4.1.0&q=85";
const LUXURY_TABLE = "https://images.unsplash.com/photo-1674818534586-78ababb77227?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBwcml2YXRlJTIwZGluaW5nJTIwYmxhY2slMjBnb2xkJTIwdGFibGUlMjBzZXR0aW5nJTIwY2FuZGxlbGlnaHR8ZW58MHx8fHwxNzcwODY1NDM2fDA&ixlib=rb-4.1.0&q=85";
const DINNER_PARTY = "https://images.unsplash.com/photo-1768508950434-fc76c9f3ed9f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwzfHxlbGVnYW50JTIwZGlubmVyJTIwcGFydHklMjBndWVzdHMlMjBsYXVnaGluZyUyMGNoYW1wYWduZSUyMGRhcmslMjBhbWJpZW50fGVufDB8fHx8MTc3MDg2NTQ2NXww&ixlib=rb-4.1.0&q=85";
const CHEF_HANDS = "https://images.unsplash.com/photo-1661505300261-d45fa3e29890?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBjaGVmJTIwaGFuZHMlMjBwbGF0aW5nJTIwZm9vZCUyMGRhcmslMjBtb29keXxlbnwwfHx8fDE3NzA4NjU0NDR8MA&ixlib=rb-4.1.0&q=85";
const CANDLELIGHT = "https://images.unsplash.com/photo-1570459467207-bda077e3e3be?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBwcml2YXRlJTIwZGluaW5nJTIwYmxhY2slMjBnb2xkJTIwdGFibGUlMjBzZXR0aW5nJTIwY2FuZGxlbGlnaHR8ZW58MHx8fHwxNzcwODY1NDM2fDA&ixlib=rb-4.1.0&q=85";

const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15 }
  }
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.6 } }
};

export const ExperiencePage = () => {
  const whatMakesUsRoyal = [
    "Soulful, deeply seasoned hibachi",
    "Luxury-level service",
    "Fully staffed events",
    "Setup to breakdown included",
    "Premium ingredients",
    "Cultural warmth and excellence"
  ];

  const howItWorks = [
    {
      step: "01",
      icon: <Calendar className="w-7 h-7" />,
      title: "Reserve Your Date",
      description: "Secure your event with a $200 Booking Fee. This guarantees your preferred date and begins the planning process."
    },
    {
      step: "02",
      icon: <UtensilsCrossed className="w-7 h-7" />,
      title: "Choose Your Experience",
      description: "Select your menu and service style — plated dinner party or royal feast buffet. We'll customize to your preferences."
    },
    {
      step: "03",
      icon: <Phone className="w-7 h-7" />,
      title: "Pre-Event Consultation",
      description: "Two weeks before your event, we finalize every detail — dietary needs, special touches, and timing."
    },
    {
      step: "04",
      icon: <Sparkles className="w-7 h-7" />,
      title: "Your Royal Experience",
      description: "We arrive fully staffed, fully prepared, and ready to create an unforgettable celebration."
    }
  ];

  const serviceStyles = [
    {
      image: LUXURY_TABLE,
      icon: <Crown className="w-8 h-8" />,
      title: "Plated Royal Dinner Party",
      description: "Refined, intimate, luxury dining with a dedicated server. Each course is artfully presented, creating an exclusive dining experience.",
      guests: "2-12 Guests",
      price: "$65-$125/person",
      highlight: "Includes Server"
    },
    {
      image: DINNER_PARTY,
      icon: <Users className="w-8 h-8" />,
      title: "Royal Feast Buffet",
      description: "A soulful, celebratory buffet perfect for families and large gatherings. Abundant, beautiful, and designed to bring people together.",
      guests: "10-40 Guests",
      price: "$55-$100/person",
      highlight: "Best for Groups"
    },
    {
      image: CHEF_HANDS,
      icon: <Leaf className="w-8 h-8" />,
      title: "Vegan Royalty",
      description: "Intentional, flavorful, elevated plant-based options crafted with the same care, technique, and luxury as our signature dishes.",
      guests: "Any Size",
      price: "From $25/person",
      highlight: "Plant-Based"
    },
    {
      image: CANDLELIGHT,
      icon: <Baby className="w-8 h-8" />,
      title: "Kids Royal Menu",
      description: "Simple, familiar, and family-friendly options that kids love. Because every member of the family deserves royal treatment.",
      guests: "Ages 8-12",
      price: "$20/child",
      highlight: "Under 7 Free"
    }
  ];

  const staffServices = [
    { name: "Setup", desc: "Complete event preparation" },
    { name: "Hospitality", desc: "Warm guest reception" },
    { name: "Service", desc: "Attentive table service" },
    { name: "Clearing", desc: "Seamless plate management" },
    { name: "Refilling", desc: "Continuous beverage service" },
    { name: "Cleanup", desc: "Thorough post-event care" },
    { name: "Breakdown", desc: "Complete tear-down" }
  ];

  return (
    <div data-testid="experience-page" className="min-h-screen bg-[#050505]">
      
      {/* ==================== HERO ==================== */}
      <section className="relative min-h-[60vh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src={HERO_IMAGE} 
            alt="The Royal Experience" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/50 to-transparent" />
        </div>

        <div className="max-w-7xl mx-auto px-6 lg:px-8 pb-24 relative z-10 w-full">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="text-center max-w-3xl mx-auto"
          >
            <motion.p variants={fadeInUp} className="text-[#D4AF37] uppercase tracking-[0.2em] text-sm mb-4 font-body">
              The Experience
            </motion.p>
            <motion.h1 variants={fadeInUp} className="font-heading text-5xl sm:text-6xl lg:text-7xl font-bold text-[#F5F5F0] mb-6">
              What Makes Us <span className="italic text-[#D4AF37]">Royal</span>
            </motion.h1>
            <motion.p variants={fadeInUp} className="font-body text-lg text-[#A1A1AA]">
              Every event is designed to feel intentional, warm, and unforgettable.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* ==================== THE ROYAL PROMISE ==================== */}
      <section className="py-24 lg:py-32 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">
            {/* Image */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="aspect-[4/5] rounded-2xl overflow-hidden">
                <img 
                  src={LUXURY_TABLE} 
                  alt="Luxury dining setup" 
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 w-48 h-48 border border-[#D4AF37]/30 rounded-2xl" />
            </motion.div>

            {/* Content */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={staggerContainer}
            >
              <motion.p variants={fadeInUp} className="text-[#D4AF37] uppercase tracking-[0.2em] text-sm mb-4 font-body">
                Our Promise
              </motion.p>
              <motion.h2 variants={fadeInUp} className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-[#F5F5F0] mb-8">
                The Royal <span className="italic">Difference</span>
              </motion.h2>
              
              <motion.div variants={staggerContainer} className="space-y-4">
                {whatMakesUsRoyal.map((item, index) => (
                  <motion.div 
                    key={index} 
                    variants={fadeInUp}
                    className="flex items-center gap-4 bg-[#121212] rounded-xl p-4 border border-white/5"
                  >
                    <div className="w-8 h-8 rounded-full bg-[#D4AF37]/20 flex items-center justify-center flex-shrink-0">
                      <Check className="w-4 h-4 text-[#D4AF37]" />
                    </div>
                    <span className="font-body text-[#F5F5F0]">{item}</span>
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ==================== HOW IT WORKS ==================== */}
      <section className="py-24 lg:py-32 bg-[#121212]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
          >
            <motion.div variants={fadeInUp} className="text-center mb-16 max-w-3xl mx-auto">
              <p className="text-[#D4AF37] uppercase tracking-[0.2em] text-sm mb-4 font-body">Process</p>
              <h2 className="font-heading text-4xl sm:text-5xl font-bold text-[#F5F5F0] mb-6">
                How It Works
              </h2>
              <p className="font-body text-lg text-[#A1A1AA]">
                From booking to breakdown, we handle every detail so you can focus on celebrating.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {howItWorks.map((step, index) => (
                <motion.div
                  key={index}
                  variants={scaleIn}
                  className="relative group"
                  data-testid={`how-it-works-${index}`}
                >
                  <div className="bg-[#050505] border border-white/5 rounded-2xl p-8 h-full hover:border-[#D4AF37]/30 transition-colors duration-500">
                    {/* Step Number */}
                    <span className="absolute top-6 right-6 font-heading text-6xl font-bold text-[#D4AF37]/10 group-hover:text-[#D4AF37]/20 transition-colors">
                      {step.step}
                    </span>
                    
                    {/* Icon */}
                    <div className="w-14 h-14 rounded-full bg-[#D4AF37]/10 flex items-center justify-center mb-6 group-hover:bg-[#D4AF37]/20 transition-colors">
                      <span className="text-[#D4AF37]">{step.icon}</span>
                    </div>
                    
                    <h3 className="font-heading text-xl font-semibold text-[#F5F5F0] mb-3">{step.title}</h3>
                    <p className="font-body text-sm text-[#A1A1AA] leading-relaxed">{step.description}</p>
                  </div>
                  
                  {/* Connector Line */}
                  {index < 3 && (
                    <div className="hidden lg:block absolute top-1/2 -right-3 w-6 h-px bg-[#D4AF37]/30" />
                  )}
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ==================== SERVICE STYLES ==================== */}
      <section className="py-24 lg:py-32 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
          >
            <motion.div variants={fadeInUp} className="mb-16">
              <p className="text-[#D4AF37] uppercase tracking-[0.2em] text-sm mb-4 font-body">Options</p>
              <h2 className="font-heading text-4xl sm:text-5xl font-bold text-[#F5F5F0]">
                Service Styles
              </h2>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {serviceStyles.map((service, index) => (
                <motion.div
                  key={index}
                  variants={scaleIn}
                  className="group relative overflow-hidden rounded-2xl bg-[#121212] border border-white/5 hover:border-[#D4AF37]/30 transition-colors duration-500"
                  data-testid={`service-style-${index}`}
                >
                  {/* Image */}
                  <div className="aspect-[16/10] relative overflow-hidden">
                    <img 
                      src={service.image} 
                      alt={service.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#121212] via-[#121212]/50 to-transparent" />
                    
                    {/* Badge */}
                    <div className="absolute top-4 right-4 bg-[#D4AF37] text-black text-xs uppercase tracking-wider px-3 py-1 rounded-full font-body font-semibold">
                      {service.highlight}
                    </div>
                  </div>
                  
                  {/* Content */}
                  <div className="p-6 lg:p-8">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-full bg-[#D4AF37]/10 flex items-center justify-center">
                        <span className="text-[#D4AF37]">{service.icon}</span>
                      </div>
                      <div>
                        <span className="text-xs text-[#A1A1AA] font-body">{service.guests}</span>
                      </div>
                    </div>
                    
                    <h3 className="font-heading text-2xl font-bold text-[#F5F5F0] mb-3">{service.title}</h3>
                    <p className="font-body text-[#A1A1AA] mb-4 leading-relaxed">{service.description}</p>
                    
                    <div className="flex items-center justify-between pt-4 border-t border-white/10">
                      <span className="font-heading text-xl text-[#D4AF37]">{service.price}</span>
                      <Link to="/menu" className="text-[#F5F5F0] hover:text-[#D4AF37] transition-colors font-body text-sm flex items-center gap-1">
                        View Menu <ArrowRight className="w-4 h-4" />
                      </Link>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ==================== ROYAL STAFF ==================== */}
      <section className="py-24 lg:py-32 bg-[#121212]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              {/* Content */}
              <motion.div variants={fadeInUp}>
                <p className="text-[#D4AF37] uppercase tracking-[0.2em] text-sm mb-4 font-body">Our Team</p>
                <h2 className="font-heading text-4xl sm:text-5xl font-bold text-[#F5F5F0] mb-6">
                  Royal Staff
                </h2>
                <p className="font-body text-lg text-[#A1A1AA] mb-8 leading-relaxed">
                  Our Royal Staff Team handles everything so you never lift a finger. We arrive 
                  over-prepared to ensure your event runs flawlessly from start to finish.
                </p>

                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-8">
                  {staffServices.map((service, index) => (
                    <div 
                      key={index}
                      className="bg-[#050505] border border-white/5 rounded-xl p-4 text-center hover:border-[#D4AF37]/20 transition-colors"
                    >
                      <p className="font-body text-sm text-[#F5F5F0] font-medium">{service.name}</p>
                      <p className="font-body text-xs text-[#A1A1AA] mt-1">{service.desc}</p>
                    </div>
                  ))}
                </div>
              </motion.div>

              {/* Pricing Card */}
              <motion.div 
                variants={scaleIn}
                className="bg-[#050505] border border-[#D4AF37]/30 rounded-2xl p-8 shadow-[0_0_60px_-20px_rgba(212,175,55,0.2)]"
              >
                <div className="flex items-center gap-3 mb-6">
                  <Crown className="w-8 h-8 text-[#D4AF37]" />
                  <h3 className="font-heading text-2xl font-bold text-[#F5F5F0]">
                    Royal Services & Preparation
                  </h3>
                </div>
                
                <p className="font-body text-[#A1A1AA] mb-8">
                  A premium service package covering equipment, fuel, setup, coordination, and breakdown.
                </p>
                
                <div className="space-y-4 border-t border-white/10 pt-6">
                  <div className="flex justify-between items-center">
                    <div>
                      <span className="font-body text-[#F5F5F0]">Royal Experience Fee</span>
                      <p className="font-body text-xs text-[#A1A1AA]">Under 30 guests</p>
                    </div>
                    <span className="font-heading text-2xl text-[#D4AF37]">$150</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <div>
                      <span className="font-body text-[#F5F5F0]">Royal Experience Fee</span>
                      <p className="font-body text-xs text-[#A1A1AA]">30+ guests</p>
                    </div>
                    <span className="font-heading text-2xl text-[#D4AF37]">$250</span>
                  </div>
                  <div className="flex justify-between items-center pt-4 border-t border-white/10">
                    <div>
                      <span className="font-body text-[#F5F5F0]">Additional Staff Member</span>
                      <p className="font-body text-xs text-[#A1A1AA]">Per person</p>
                    </div>
                    <span className="font-heading text-2xl text-[#D4AF37]">$125</span>
                  </div>
                </div>
                
                <p className="font-body text-xs text-[#A1A1AA] mt-6 text-center">
                  Plated dinners include a dedicated server
                </p>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ==================== CTA ==================== */}
      <section className="py-24 lg:py-32 bg-[#050505]">
        <motion.div 
          className="max-w-4xl mx-auto px-6 lg:px-8 text-center"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
        >
          <motion.h2 
            variants={fadeInUp}
            className="font-heading text-4xl sm:text-5xl font-bold text-[#F5F5F0] mb-6"
          >
            Ready to Begin Your <span className="italic text-[#D4AF37]">Royal Journey</span>?
          </motion.h2>
          <motion.p 
            variants={fadeInUp}
            className="font-body text-lg text-[#A1A1AA] mb-10 max-w-2xl mx-auto"
          >
            Secure your date with a $200 booking fee and let's create something unforgettable together.
          </motion.p>
          <motion.div variants={fadeInUp} className="flex flex-wrap gap-4 justify-center">
            <Link to="/booking">
              <Button 
                data-testid="experience-book-btn"
                className="bg-[#D4AF37] text-black hover:bg-[#C5A028] font-semibold rounded-full px-10 py-6 text-base transition-all duration-300 hover:scale-105 shadow-[0_0_30px_-10px_rgba(212,175,55,0.5)]"
              >
                Reserve Your Date
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/menu">
              <Button 
                variant="outline"
                data-testid="experience-menu-btn"
                className="border border-white/20 text-[#F5F5F0] hover:bg-white/10 bg-transparent rounded-full px-10 py-6 text-base transition-all duration-300"
              >
                View Menu
              </Button>
            </Link>
          </motion.div>
        </motion.div>
      </section>
    </div>
  );
};

export default ExperiencePage;
